import React, { useMemo, useRef, useState } from "react";
import { Autocomplete } from "@react-google-maps/api";
import { Navigation, ChevronDown, ChevronUp, Search, Lock } from "lucide-react";
import TRUCK_TYPES from "../../../assets/truck_types.js";
import US_STATES from "../../../assets/us_states.js";

const SearchForm = ({
  form,
  onSubmit,
  onGetCurrentLocation,
  isSearchLoading,
  isTrialLimitReached,
}) => {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = form;

  const [originAutocomplete, setOriginAutocomplete] = useState(null);
  const [destinationAutocomplete, setDestinationAutocomplete] = useState(null);
  const [showTruckDropdown, setShowTruckDropdown] = useState(false);
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const truckDropdownRef = useRef(null);

  const values = watch();
  const truckTypes = useMemo(
    () => values.truck_types || [],
    [values.truck_types],
  );

  const handleTruckTypeToggle = (value) => {
    const exists = truckTypes.includes(value);
    const next = exists
      ? truckTypes.filter((type) => type !== value)
      : [...truckTypes, value];
    setValue("truck_types", next, { shouldDirty: true });
  };

  const handleOriginPlaceChanged = () => {
    if (!originAutocomplete) return;
    const place = originAutocomplete.getPlace();
    if (!place) return;
    const cityComponent = place.address_components?.find((component) =>
      component.types.includes("locality"),
    );
    const stateComponent = place.address_components?.find((component) =>
      component.types.includes("administrative_area_level_1"),
    );
    if (cityComponent) {
      setValue("origin_city", cityComponent.long_name, { shouldDirty: true });
    }
    if (stateComponent) {
      setValue("origin_state", stateComponent.short_name, { shouldDirty: true });
    }
  };

  const handleDestinationPlaceChanged = () => {
    if (!destinationAutocomplete) return;
    const place = destinationAutocomplete.getPlace();
    if (!place) return;
    const cityComponent = place.address_components?.find((component) =>
      component.types.includes("locality"),
    );
    const stateComponent = place.address_components?.find((component) =>
      component.types.includes("administrative_area_level_1"),
    );
    if (cityComponent) {
      setValue("destination_city", cityComponent.long_name, {
        shouldDirty: true,
      });
    }
    if (stateComponent) {
      setValue("destination_state", stateComponent.short_name, {
        shouldDirty: true,
      });
    }
  };

  const truckTypeLabel = useMemo(() => {
    if (!truckTypes.length) {
      return "Select truck types";
    }
    return truckTypes
      .map(
        (val) => TRUCK_TYPES.find((type) => type.value === val)?.label || val,
      )
      .join(", ");
  }, [truckTypes]);

  return (
    <form onSubmit={onSubmit} className="bg-white border-b border-gray-200">
      <div className="px-5 py-2.5">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4 md:gap-4 mb-3">
          <div className="flex flex-col">
            <label className="text-base font-medium text-tw-neutral-700 mb-0.5">
              Pickup City:
            </label>
            <div className="relative">
              <Autocomplete
                onLoad={(instance) => setOriginAutocomplete(instance)}
                onPlaceChanged={handleOriginPlaceChanged}
                restrictions={{ country: "us" }}
              >
                <input
                  {...register("origin_city")}
                  className="px-2.5 py-1 border border-tw-neutral-300 rounded-sm text-base w-full bg-white text-tw-neutral-900 focus:outline-none focus:ring-2 focus:ring-tw-info focus:border-tw-info pr-10"
                  placeholder="Enter a location"
                />
              </Autocomplete>
              <Navigation
                onClick={onGetCurrentLocation}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-tw-info cursor-pointer hover:text-tw-info-600"
                role="button"
                tabIndex={0}
                aria-label="Use current location for pickup city"
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onGetCurrentLocation();
                  }
                }}
              />
            </div>
            {errors.origin_city && (
              <span className="text-xs text-tw-danger mt-0.5">
                {errors.origin_city.message}
              </span>
            )}
          </div>

          <div className="flex flex-col">
            <label className="text-base font-medium text-tw-neutral-700 mb-0.5">
              Delivery City:
            </label>
            <Autocomplete
              onLoad={(instance) => setDestinationAutocomplete(instance)}
              onPlaceChanged={handleDestinationPlaceChanged}
              restrictions={{ country: "us" }}
            >
              <input
                {...register("destination_city")}
                className="px-2.5 py-1 border border-tw-neutral-300 rounded-sm text-base w-full bg-white text-tw-neutral-900 focus:outline-none focus:ring-2 focus:ring-tw-info focus:border-tw-info"
                placeholder="Anywhere"
              />
            </Autocomplete>
          </div>

          <div className="flex flex-col relative" ref={truckDropdownRef}>
            <label className="text-base font-medium text-tw-neutral-700 mb-0.5">
              Trailer Types:
            </label>
            <button
              type="button"
              onClick={() => setShowTruckDropdown((prev) => !prev)}
              className="px-2.5 py-1 border border-tw-neutral-300 rounded-sm text-base bg-white text-tw-neutral-900 focus:outline-none focus:ring-2 focus:ring-tw-info focus:border-tw-info text-left flex items-center justify-between w-full"
            >
              <span className="truncate">{truckTypeLabel}</span>
              <ChevronDown className="h-4 w-4 flex-shrink-0 ml-2 text-tw-neutral-600" />
            </button>
            {showTruckDropdown && (
              <div className="absolute top-full left-0 right-0 z-10 mt-1 bg-white border border-tw-neutral-300 rounded-sm max-h-60 overflow-auto">
                {[...TRUCK_TYPES]
                  .sort((a, b) => a.label.localeCompare(b.label))
                  .map((type) => (
                    <label
                      key={type.value}
                      className="flex items-center px-3 py-2 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={truckTypes.includes(type.value)}
                        onChange={() => handleTruckTypeToggle(type.value)}
                        className="mr-2"
                      />
                      <span className="text-base text-tw-neutral-900">
                        {type.label}
                      </span>
                    </label>
                  ))}
              </div>
            )}
          </div>

          <div className="flex flex-col">
            <label className="text-base font-medium text-tw-neutral-700 mb-0.5">
              Ship Date:
            </label>
            <input
              type="date"
              {...register("ship_date")}
              className="px-2.5 py-1 border border-tw-neutral-300 rounded-sm text-base w-full bg-white text-tw-neutral-900 focus:outline-none focus:ring-2 focus:ring-tw-info focus:border-tw-info"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-4 md:gap-4 mb-3">
          <div className="flex flex-col">
            <label className="text-base font-medium text-tw-neutral-700 mb-0.5">
              Pickup State:
            </label>
            <select
              {...register("origin_state")}
              className="px-2.5 py-1 border border-tw-neutral-300 rounded-sm text-base bg-white text-tw-neutral-900 focus:outline-none focus:ring-2 focus:ring-tw-info focus:border-tw-info"
            >
              {US_STATES.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-base font-medium text-tw-neutral-700 mb-0.5">
              Delivery State:
            </label>
            <select
              {...register("destination_state")}
              className="px-2.5 py-1 border border-tw-neutral-300 rounded-sm text-base bg-white text-tw-neutral-900 focus:outline-none focus:ring-2 focus:ring-tw-info focus:border-tw-info"
            >
              <option value="">Any</option>
              {US_STATES.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-base font-medium text-tw-neutral-700 mb-0.5">
              Max Weight per Load (lbs):
            </label>
            <input
              type="number"
              {...register("max_weight", { valueAsNumber: true })}
              className="px-2.5 py-1 border border-tw-neutral-300 rounded-sm text-base w-full bg-white text-tw-neutral-900 focus:outline-none focus:ring-2 focus:ring-tw-info focus:border-tw-info"
            />
          </div>

          <div className="flex flex-col">
            <label className="text-base font-medium text-tw-neutral-700 mb-0.5">
              Min Distance:
            </label>
            <input
              type="number"
              {...register("min_distance", { valueAsNumber: true })}
              className="px-2.5 py-1 border border-tw-neutral-300 rounded-sm text-base w-full bg-white text-tw-neutral-900 focus:outline-none focus:ring-2 focus:ring-tw-info focus:border-tw-info"
            />
          </div>
        </div>

        {/* More Options Toggle - Mobile Only */}
        <button
          type="button"
          onClick={() => setShowMoreOptions(!showMoreOptions)}
          className="md:hidden flex items-center justify-center gap-2 w-full py-2.5 text-sm font-medium text-tw-neutral-700 bg-tw-neutral-50 hover:bg-tw-neutral-100 border border-tw-neutral-200 rounded transition-colors"
        >
          {showMoreOptions ? (
            <>
              <ChevronUp className="h-4 w-4" />
              <span>Less Options</span>
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4" />
              <span>More Options</span>
            </>
          )}
        </button>

        {/* Advanced Fields - Collapsible on mobile, always visible on desktop */}
        <div className={`${showMoreOptions ? 'block' : 'hidden'} md:block`}>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4 md:gap-4">
          {!values.enableBackhaul ? (
            <div className="flex flex-col">
              <label className="text-base font-medium text-tw-neutral-700 mb-0.5">
                DH-Origin (miles):
              </label>
              <input
                type="number"
                {...register("origin_range", { valueAsNumber: true })}
                className="px-2.5 py-1 border border-tw-neutral-300 rounded-sm text-base w-full bg-white text-tw-neutral-900 focus:outline-none focus:ring-2 focus:ring-tw-info focus:border-tw-info"
              />
            </div>
          ) : (
            <div className="hidden md:block" aria-hidden="true" />
          )}

          {!values.enableBackhaul ? (
            <div className="flex flex-col">
              <label className="text-base font-medium text-tw-neutral-700 mb-0.5">
                DH-Destination (miles):
              </label>
              <input
                type="number"
                {...register("destination_range", { valueAsNumber: true })}
                className="px-2.5 py-1 border border-tw-neutral-300 rounded-sm text-base w-full bg-white text-tw-neutral-900 focus:outline-none focus:ring-2 focus:ring-tw-info focus:border-tw-info"
              />
            </div>
          ) : (
            <div className="hidden md:block" aria-hidden="true" />
          )}

          <div className="flex flex-col">
            <label className="text-base font-medium text-tw-neutral-700 mb-0.5">
              Max Length per Load (feet):
            </label>
            <input
              type="number"
              {...register("max_length", { valueAsNumber: true })}
              className="px-2.5 py-1 border border-tw-neutral-300 rounded-sm text-base w-full bg-white text-tw-neutral-900 focus:outline-none focus:ring-2 focus:ring-tw-info focus:border-tw-info"
            />
          </div>

          <div className="flex flex-col">
            <label className="text-base font-medium text-tw-neutral-700 mb-0.5">
              Max Distance:
            </label>
            <input
              type="number"
              {...register("max_distance", { valueAsNumber: true })}
              className="px-2.5 py-1 border border-tw-neutral-300 rounded-sm text-base w-full bg-white text-tw-neutral-900 focus:outline-none focus:ring-2 focus:ring-tw-info focus:border-tw-info"
              placeholder="Unlimited"
            />
          </div>
        </div>

        <div className="flex items-center justify-between mt-3">
          <label className="flex items-center gap-2 text-base text-tw-neutral-700">
            <input
              type="checkbox"
              {...register("enableBackhaul")}
              className="rounded border-tw-neutral-300 text-tw-secondary focus:ring-tw-secondary"
            />
            <span>Enable Backhaul Search</span>
          </label>
          </div>
        </div>

        <div className="flex justify-end mt-3">
          <button
            type="submit"
            disabled={isSearchLoading || isTrialLimitReached}
            className={`w-full md:w-auto px-5 py-1.5 bg-tw-info text-white rounded-sm text-base ${
              isSearchLoading || isTrialLimitReached
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-[#1a73e8]"
            } flex items-center justify-center gap-2 font-medium whitespace-nowrap transition-colors`}
          >
            {isSearchLoading ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                <span>Searching...</span>
              </>
            ) : isTrialLimitReached ? (
              <>
                <Lock className="h-4 w-4" />
                <span>Limit Reached</span>
              </>
            ) : (
              <>
                <Search className="h-4 w-4" />
                <span>Search</span>
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default SearchForm;

